import { GREETING_MOODS, TIME_BLOCKS } from '../types/greeting'
import {
	AFTERNOON_MESSAGES,
	EVENING_COMPLETE_MESSAGES,
	EVENING_MESSAGES,
	FLAGGED_MESSAGES,
	FRIDAY_AFTERNOON_MESSAGES,
	GREETING_GRADIENTS,
	GREETING_ICONS,
	LATE_LOGIN_MESSAGES,
	MONDAY_MORNING_MESSAGES,
	MORNING_HEAVY_MESSAGES,
	MORNING_LIGHT_MESSAGES,
	MORNING_MESSAGES,
	NIGHT_MESSAGES,
} from '../constants/greetingMessages'

const LAST_MESSAGE_KEY = 'mypeegu_greeting_last_message'

/** @param {Date} date */
export function getTimeBlock(date = new Date()) {
	const hour = date.getHours()
	if (hour >= 5 && hour < 12) return TIME_BLOCKS.MORNING
	if (hour >= 12 && hour < 17) return TIME_BLOCKS.AFTERNOON
	if (hour >= 17 && hour <= 21) return TIME_BLOCKS.EVENING
	return TIME_BLOCKS.NIGHT
}

/** @param {string} fullName */
export function getFirstName(fullName = '') {
	const cleaned = String(fullName || '').trim()
	if (!cleaned) return 'Counsellor'
	return cleaned.split(/\s+/)[0]
}

/** @param {Date|string|number} value */
export function toDate(value) {
	if (!value) return null
	const date = value instanceof Date ? value : new Date(value)
	return Number.isNaN(date.getTime()) ? null : date
}

/** @param {Date} date */
export function formatSessionTime(date) {
	return date.toLocaleTimeString('en-IN', {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	})
}

/**
 * Pick a message from a pool without repeating the last shown message consecutively.
 * @param {string[]} pool
 * @param {string} [poolKey]
 */
export function pickRotatingMessage(pool, poolKey = 'default') {
	if (!Array.isArray(pool) || pool.length === 0) return ''
	if (pool.length === 1) return pool[0]

	let lastMap = {}
	try {
		lastMap = JSON.parse(sessionStorage.getItem(LAST_MESSAGE_KEY) || '{}') || {}
	} catch {
		lastMap = {}
	}

	const last = lastMap[poolKey]
	const candidates = pool.filter((msg) => msg !== last)
	const chosen = candidates[Math.floor(Math.random() * candidates.length)] || pool[0]

	try {
		lastMap[poolKey] = chosen
		sessionStorage.setItem(LAST_MESSAGE_KEY, JSON.stringify(lastMap))
	} catch {
		/* ignore quota */
	}

	return chosen
}

/**
 * @param {import('../types/greeting').GreetingSession[]} sessions
 * @param {Date} now
 */
export function deriveSessionStats(sessions = [], now = new Date()) {
	const todayStart = new Date(now)
	todayStart.setHours(0, 0, 0, 0)
	const todayEnd = new Date(now)
	todayEnd.setHours(23, 59, 59, 999)
	const noon = new Date(now)
	noon.setHours(12, 0, 0, 0)

	const todays = sessions
		.map((session) => {
			const start =
				toDate(session.scheduledTime) ||
				toDate(session.startTime) ||
				toDate(session.time)
			return start
				? {
						...session,
						_start: start,
						studentName:
							session.studentName ||
							session.student?.fullName ||
							session.title ||
							'Student',
						flagged: Boolean(
							session.flagged ||
								session.isFlagged ||
								session.student?.flagged ||
								session.priority === 'high',
						),
					}
				: null
		})
		.filter(Boolean)
		.filter((session) => session._start >= todayStart && session._start <= todayEnd)
		.sort((a, b) => a._start - b._start)

	const remaining = todays.filter(
		(session) =>
			session._start >= now &&
			!['ended', 'expired', 'cancelled', 'completed'].includes(
				String(session.status || '').toLowerCase(),
			),
	)
	const completed = todays.filter(
		(session) =>
			session._start < now ||
			['ended', 'completed'].includes(String(session.status || '').toLowerCase()),
	)
	const beforeLunch = todays.filter((session) => session._start < noon)
	const nextSession = remaining[0] || null

	// Continuous streak: consecutive upcoming/completed blocks within 90 min gaps
	let continuous = 0
	let streak = 0
	for (let i = 0; i < todays.length; i += 1) {
		if (i === 0) {
			streak = 1
		} else {
			const gap = todays[i]._start - todays[i - 1]._start
			streak = gap <= 90 * 60 * 1000 ? streak + 1 : 1
		}
		continuous = Math.max(continuous, streak)
	}

	return {
		todaysSessions: todays,
		sessionsBeforeLunch: beforeLunch.length,
		remainingSessions: remaining.length,
		completedSessions: completed.length,
		nextSession,
		hasFlaggedUpcoming: remaining.some((session) => session.flagged),
		continuousSessionStreak: continuous,
	}
}

/**
 * @param {import('../types/greeting').GreetingContext} ctx
 */
function buildChip(ctx) {
	const {
		calendarAvailable,
		remainingSessions,
		sessionsBeforeLunch,
		pendingNotes,
		nextSession,
		currentTime,
	} = ctx

	if (!calendarAvailable) {
		return {
			chipText: 'No sessions today',
			chipSecondary: 'Use this time wisely.',
		}
	}

	if (remainingSessions === 0) {
		return {
			chipText: 'All sessions complete',
			chipSecondary:
				pendingNotes > 0
					? `${pendingNotes} note${pendingNotes === 1 ? '' : 's'} pending`
					: 'A good moment to breathe.',
		}
	}

	const timeBlock = getTimeBlock(currentTime)
	if (timeBlock === TIME_BLOCKS.MORNING && sessionsBeforeLunch >= 1) {
		return {
			chipText: `${sessionsBeforeLunch} session${sessionsBeforeLunch === 1 ? '' : 's'} before lunch`,
			chipSecondary: nextSession
				? `Next: ${nextSession.studentName} · ${formatSessionTime(nextSession._start)}`
				: undefined,
		}
	}

	return {
		chipText: `${remainingSessions} session${remainingSessions === 1 ? '' : 's'} remaining`,
		chipSecondary: nextSession
			? `${nextSession.studentName} · ${formatSessionTime(nextSession._start)}`
			: undefined,
	}
}

/**
 * @param {import('../types/greeting').GreetingContext} ctx
 * @returns {import('../types/greeting').GreetingResult}
 */
export function buildGreeting(ctx) {
	const now = ctx.currentTime || new Date()
	const timeBlock = getTimeBlock(now)
	const firstName = getFirstName(ctx.userName)
	const day = now.getDay() // 0 Sun … 1 Mon … 5 Fri
	const hour = now.getHours()

	/** @type {string} */
	let title = `Hello, ${firstName}`
	/** @type {string} */
	let message = ''
	/** @type {import('../types/greeting').GreetingMood} */
	let mood = GREETING_MOODS.CALM
	let poolKey = `${timeBlock}-default`

	if (timeBlock === TIME_BLOCKS.MORNING) {
		title = `Good morning, ${firstName}`
		mood = GREETING_MOODS.ENERGISED
	} else if (timeBlock === TIME_BLOCKS.AFTERNOON) {
		title = `Good afternoon, ${firstName}`
		mood = GREETING_MOODS.SUPPORTIVE
	} else if (timeBlock === TIME_BLOCKS.EVENING) {
		title = `Good evening, ${firstName}`
		mood = GREETING_MOODS.REFLECTIVE
	} else {
		title = `Working late, ${firstName}?`
		mood = GREETING_MOODS.REST
	}

	// Priority cascade
	if (timeBlock === TIME_BLOCKS.MORNING && day === 1) {
		message = pickRotatingMessage(MONDAY_MORNING_MESSAGES, 'monday-morning')
		poolKey = 'monday-morning'
		mood = GREETING_MOODS.ENERGISED
	} else if (ctx.hasFlaggedUpcoming) {
		message = pickRotatingMessage(FLAGGED_MESSAGES, 'flagged')
		poolKey = 'flagged'
		mood = GREETING_MOODS.SUPPORTIVE
	} else if (
		(timeBlock === TIME_BLOCKS.EVENING || timeBlock === TIME_BLOCKS.NIGHT) &&
		hour >= 19
	) {
		message = pickRotatingMessage(LATE_LOGIN_MESSAGES, 'late-login')
		poolKey = 'late-login'
		mood = GREETING_MOODS.REST
	} else if (
		timeBlock === TIME_BLOCKS.EVENING &&
		ctx.calendarAvailable &&
		ctx.remainingSessions === 0 &&
		ctx.completedSessions > 0
	) {
		message = pickRotatingMessage(EVENING_COMPLETE_MESSAGES, 'evening-complete')
		poolKey = 'evening-complete'
		mood = GREETING_MOODS.REFLECTIVE
	} else if (timeBlock === TIME_BLOCKS.AFTERNOON && day === 5) {
		message = pickRotatingMessage(FRIDAY_AFTERNOON_MESSAGES, 'friday-afternoon')
		poolKey = 'friday-afternoon'
		mood = GREETING_MOODS.REFLECTIVE
	} else if (
		timeBlock === TIME_BLOCKS.MORNING &&
		ctx.calendarAvailable &&
		ctx.sessionsBeforeLunch >= 3
	) {
		message = pickRotatingMessage(MORNING_HEAVY_MESSAGES, 'morning-heavy')
		poolKey = 'morning-heavy'
		mood = GREETING_MOODS.SUPPORTIVE
	} else if (
		timeBlock === TIME_BLOCKS.MORNING &&
		ctx.calendarAvailable &&
		ctx.todaysSessions?.length <= 1
	) {
		message = pickRotatingMessage(MORNING_LIGHT_MESSAGES, 'morning-light')
		poolKey = 'morning-light'
		mood = GREETING_MOODS.CALM
	} else if (
		timeBlock === TIME_BLOCKS.AFTERNOON &&
		ctx.calendarAvailable &&
		ctx.remainingSessions > 0
	) {
		message = pickRotatingMessage(AFTERNOON_MESSAGES, 'afternoon')
		poolKey = 'afternoon'
	} else if (timeBlock === TIME_BLOCKS.MORNING) {
		// Client morning lines are calendar-free by design — always rotate this pool.
		message = pickRotatingMessage(MORNING_MESSAGES, 'morning')
		poolKey = 'morning'
	} else if (timeBlock === TIME_BLOCKS.AFTERNOON) {
		message = pickRotatingMessage(AFTERNOON_MESSAGES, 'afternoon-fallback')
		poolKey = 'afternoon-fallback'
	} else if (timeBlock === TIME_BLOCKS.EVENING) {
		message = pickRotatingMessage(EVENING_MESSAGES, 'evening')
		poolKey = 'evening'
	} else {
		message = pickRotatingMessage(NIGHT_MESSAGES, 'night')
		poolKey = 'night'
	}

	const chip = buildChip(ctx)

	return {
		title,
		message,
		chipText: chip.chipText,
		chipSecondary: chip.chipSecondary,
		gradient: GREETING_GRADIENTS[timeBlock],
		icon: GREETING_ICONS[timeBlock],
		mood,
		timeBlock,
		showBreakReminder: Boolean(ctx.continuousSessionStreak >= 4),
		_poolKey: poolKey,
	}
}
